import React, { useState, useCallback, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Trophy, Play, RotateCcw } from "lucide-react";

interface SimonSaysProps {
  t: (key: string) => string;
}

type GameState = 'idle' | 'showing' | 'playing' | 'won' | 'lost';

const COLORS = [
  { id: 0, bg: 'bg-red-500', activeBg: 'bg-red-300', name: 'red' },
  { id: 1, bg: 'bg-blue-500', activeBg: 'bg-blue-300', name: 'blue' },
  { id: 2, bg: 'bg-green-500', activeBg: 'bg-green-300', name: 'green' },
  { id: 3, bg: 'bg-yellow-500', activeBg: 'bg-yellow-300', name: 'yellow' },
];

// Extend Window interface for webkit prefixed AudioContext
interface WebkitWindow extends Window {
  webkitAudioContext?: typeof AudioContext;
}

const playSound = (frequency: number, duration: number = 200) => {
  try {
    const AudioContextClass = window.AudioContext || (window as WebkitWindow).webkitAudioContext;
    if (!AudioContextClass) return;
    
    const audioContext = new AudioContextClass();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration / 1000);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration / 1000);
  } catch {
    // Audio not available - silently ignore
  }
};

const FREQUENCIES = [329.63, 261.63, 220, 164.81];

export function SimonSays({ t }: SimonSaysProps) {
  const [sequence, setSequence] = useState<number[]>([]);
  const [playerSequence, setPlayerSequence] = useState<number[]>([]);
  const [gameState, setGameState] = useState<GameState>('idle');
  const [activeColor, setActiveColor] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const showingRef = useRef(false);

  const flashColor = useCallback((colorId: number, duration: number = 400) => {
    return new Promise<void>((resolve) => {
      setActiveColor(colorId);
      playSound(FREQUENCIES[colorId], duration);
      setTimeout(() => {
        setActiveColor(null);
        setTimeout(resolve, 100);
      }, duration);
    });
  }, []);

  const showSequence = useCallback(async () => {
    if (showingRef.current) return;
    showingRef.current = true;
    setGameState('showing');
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    for (const colorId of sequence) {
      await flashColor(colorId);
    }
    
    showingRef.current = false;
    setGameState('playing');
    setPlayerSequence([]);
  }, [sequence, flashColor]);

  const addToSequence = useCallback(() => {
    const newColor = Math.floor(Math.random() * 4);
    setSequence(prev => [...prev, newColor]);
  }, []);

  const startGame = useCallback(() => {
    setScore(0);
    setSequence([Math.floor(Math.random() * 4)]);
    setPlayerSequence([]);
    setGameState('showing');
  }, []);

  const handleColorClick = useCallback(async (colorId: number) => {
    if (gameState !== 'playing') return;
    
    await flashColor(colorId, 200);
    
    setPlayerSequence(prevPlayerSequence => {
      const newPlayerSequence = [...prevPlayerSequence, colorId];
      
      const currentIndex = newPlayerSequence.length - 1;
      if (newPlayerSequence[currentIndex] !== sequence[currentIndex]) {
        setGameState('lost');
        setHighScore(prev => Math.max(prev, score));
        return newPlayerSequence;
      }
      
      if (newPlayerSequence.length === sequence.length) {
        const newScore = score + 1;
        setScore(newScore);
        
        if (newScore >= 10) {
          setGameState('won');
          setHighScore(prev => Math.max(prev, newScore));
          return newPlayerSequence;
        }
        
        setTimeout(() => {
          addToSequence();
        }, 500);
      }
      
      return newPlayerSequence;
    });
  }, [gameState, sequence, score, flashColor, addToSequence]);

  const resetGame = useCallback(() => {
    setSequence([]);
    setPlayerSequence([]);
    setGameState('idle');
    setScore(0);
    setActiveColor(null);
    showingRef.current = false;
  }, []);

  useEffect(() => {
    if (sequence.length > 0 && gameState === 'showing' && !showingRef.current) {
      showSequence();
    }
  }, [sequence, gameState, showSequence]);

  return (
    <div className="space-y-6" dir="ltr">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <span className="text-lg text-muted-foreground">{t("score")}: {score}</span>
          <span className="text-sm text-muted-foreground">({t("highScore")}: {highScore})</span>
        </div>
        <Button
          onClick={gameState === 'idle' ? startGame : resetGame}
          variant="outline"
          className="gap-2"
          data-scan-item
        >
          {gameState === 'idle' ? (
            <>
              <Play className="w-4 h-4" />
              {t("startGame")}
            </>
          ) : (
            <>
              <RotateCcw className="w-4 h-4" />
              {t("newGame")}
            </>
          )}
        </Button>
      </div>

      {(gameState === 'won' || gameState === 'lost') && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className={cn(
            "flex items-center justify-center gap-3 p-4 rounded-xl border",
            gameState === 'won' ? "bg-success/20 border-success" : "bg-destructive/20 border-destructive"
          )}
        >
          <Trophy className={cn("w-6 h-6", gameState === 'won' ? "text-success" : "text-destructive")} />
          <span className={cn("text-xl font-bold", gameState === 'won' ? "text-success" : "text-destructive")}>
            {gameState === 'won' ? t("youWon") : t("gameOver")} - {t("score")}: {score}
          </span>
        </motion.div>
      )}

      {gameState === 'showing' && (
        <div className="text-center text-lg font-medium text-primary animate-pulse">
          {t("watchTheSequence")}
        </div>
      )}

      {gameState === 'playing' && (
        <div className="text-center text-lg font-medium text-muted-foreground">
          {t("yourTurn")} ({playerSequence.length + 1}/{sequence.length})
        </div>
      )}

      <div className="max-w-xs mx-auto">
        <div className="grid grid-cols-2 gap-3">
          {COLORS.map((color) => {
            const isActive = activeColor === color.id;
            const isDisabled = gameState !== 'playing' && gameState !== 'idle';

            return (
              <motion.button
                key={color.id}
                onClick={() => handleColorClick(color.id)}
                whileHover={!isDisabled ? { scale: 1.05 } : {}}
                whileTap={!isDisabled ? { scale: 0.95 } : {}}
                disabled={isDisabled}
                className={cn(
                  "aspect-square rounded-2xl transition-all duration-150 border-4 border-transparent",
                  isActive ? color.activeBg : color.bg,
                  isActive && "scale-110 shadow-xl",
                  isDisabled && "opacity-50 cursor-not-allowed"
                )}
                data-scan-item
              />
            );
          })}
        </div>
      </div>

      <p className="text-center text-sm text-muted-foreground">
        {t("simonSaysHint")}
      </p>
    </div>
  );
}
